import React from 'react';
import { RefreshCw } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { MONTHLY_DATA, INITIAL_OVERVIEW, formatINR } from '@/data/mockData';

const INTAS_LOGO = 'https://customer-assets.emergentagent.com/job_womens-health-app-16/artifacts/cbwcoejw_logo_mSBU.png';
const SWISHX_LOGO = 'https://customer-assets.emergentagent.com/job_womens-health-app-16/artifacts/ftvninlx_swishX_logo1.png';

function IntasLogo() {
  return (
    <div className="flex items-start gap-2">
      <div>
        <div style={{ height: '26px', overflow: 'hidden' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_womens-health-app-16/artifacts/eujbvqtt_intas-logo-596B341903-seeklogo.com-removebg-preview.png"
            alt="INTAS"
            className="block"
            style={{ height: '52px', width: 'auto', filter: 'brightness(0) invert(1)', marginTop: '-12px' }}
          />
        </div>
        <div className="text-white font-black text-lg leading-tight">mSBU</div>
        <div className="text-[#FF7043] font-black text-xs tracking-widest">CONNECT</div>
      </div>
    </div>
  );
}

function ProgressBar({ pct, className = '' }) {
  return (
    <div className={`relative mb-1 ${className}`}>
      <div className="h-6 bg-[#E2E8F0] rounded-full overflow-hidden relative">
        <div
          className="h-full bg-[#1E2744] rounded-full transition-all duration-700 flex items-center justify-end pr-2.5"
          style={{ width: `${pct}%` }}
        >
          {pct >= 15 && (
            <span className="text-white text-xs font-black">{pct}%</span>
          )}
        </div>
      </div>
      {pct < 15 && (
        <span className="absolute right-0 top-0 h-6 flex items-center bg-[#1E2744] text-white text-xs font-black px-2 rounded-full">
          {pct}%
        </span>
      )}
    </div>
  );
}

function PurchaseOverviewCard() {
  const { navigate, pendingValue, completionPct } = useApp();
  const { thisMonthPurchase, fyPurchase } = INITIAL_OVERVIEW;

  // Total pending across all months — dynamically updates as current month invoices are uploaded
  const totalAllPending = MONTHLY_DATA.reduce((s, m) => s + m.pending, 0);
  const currentMonthBasePending = MONTHLY_DATA[MONTHLY_DATA.length - 1].pending;
  const totalPendingValue = totalAllPending - (currentMonthBasePending - pendingValue);

  return (
    <div className="mx-4 -mt-8 bg-white rounded-2xl shadow-lg p-5 relative z-20 slide-up">
      <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Purchase Overview</div>

      {/* Row 1: This Month + FY Purchase */}
      <div className="flex gap-3">
        <div className="flex-1">
          <div className="text-xs text-slate-400 font-medium">This Month</div>
          <div className="rupee-value text-2xl text-[#1E2744] leading-tight">
            ₹{formatINR(thisMonthPurchase)}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <RefreshCw size={10} color="#94A3B8" />
            <span className="text-[10px] text-slate-400">Synced 2 mins ago</span>
          </div>
        </div>
        <div className="w-px bg-slate-200 self-stretch" />
        <div className="flex-1">
          <div className="text-xs text-slate-400 font-medium">FY Purchase</div>
          <div className="rupee-value text-2xl text-[#FF7043] leading-tight">
            ₹{formatINR(fyPurchase)}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-100 my-4" />

      {/* Row 2: Total pending upload */}
      <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
        Total value of invoices pending upload
      </div>
      <ProgressBar pct={completionPct} />
      <div className="text-[#F57C00] font-semibold text-sm mb-3">
        ₹{formatINR(totalPendingValue)} still pending upload
      </div>
      <button
        data-testid="home-upload-invoices-btn"
        onClick={() => navigate('UPLOAD_STATEMENT')}
        className="w-full bg-[#1E2744] text-white rounded-xl py-3.5 font-bold text-sm tracking-wide tap-scale"
      >
        Upload Invoices
      </button>
    </div>
  );
}

function MonthlyCard({ data, onViewAll, dynamicPct }) {
  const displayPct = dynamicPct !== undefined ? dynamicPct : data.pct;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 slide-up-d1">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-bold text-[#1E2744] text-base">{data.month}</div>
          <div className="text-xs text-slate-400">{data.syncLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-[#2563EB]">{displayPct}% COMPLETE</span>
          <button
            data-testid={`monthly-card-view-all-${data.month.replace(' ', '-')}`}
            onClick={() => onViewAll(data.month)}
            className="text-xs font-semibold text-[#FF7043] bg-orange-50 px-2.5 py-1 rounded-lg tap-scale"
          >
            View All
          </button>
        </div>
      </div>
      <div className="flex justify-between mb-2">
        <div>
          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">SALE</div>
          <div className="rupee-value font-bold text-[#1E2744] text-sm">₹{formatINR(data.sale)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">UPLOADED</div>
          <div className="rupee-value font-bold text-[#2563EB] text-sm">₹{formatINR(data.uploaded)}</div>
        </div>
      </div>
      <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden mb-1.5">
        <div
          className="h-full progress-gradient rounded-full transition-all duration-700"
          style={{ width: `${displayPct}%` }}
        />
      </div>
      <div className="flex justify-between items-center">
        <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">PENDING</div>
        <div className="rupee-value font-bold text-[#F57C00] text-sm">₹{formatINR(data.pending)}</div>
      </div>
    </div>
  );
}

export default function HomeScreen() {
  const { navigate, completionPct } = useApp();
  const sortedMonths = [...MONTHLY_DATA].reverse();

  const handleViewAll = (month) => {
    navigate('PENDING_INVOICES', { filterMonth: month });
  };

  return (
    <div className="pb-4">
      {/* Blue Header */}
      <div className="bg-[#2563EB] px-5 pt-5 pb-16 rounded-b-[2.5rem] relative">
        <div className="flex items-center justify-between">
          {/* Left: Intas mSBU Connect logo */}
          <img
            src={INTAS_LOGO}
            alt="INTAS mSBU Connect"
            className="h-16 w-auto"
            style={{ mixBlendMode: 'screen' }}
          />
          {/* Right: SwishX logo */}
          <img
            src={SWISHX_LOGO}
            alt="Powered by SwishX"
            className="h-10 w-auto"
            style={{ mixBlendMode: 'screen' }}
          />
        </div>
        <div className="mt-2">
          <div className="text-white font-semibold text-sm">Hi ANAND MEDICAL AGENCY</div>
          <div className="text-white/60 text-xs mt-0.5">Ahmedabad | Stockist · Active SBU: mSBU</div>
        </div>
      </div>

      {/* Purchase Overview Card */}
      <PurchaseOverviewCard />

      {/* Monthly Upload Summary */}
      <div className="px-4 mt-6">
        <div className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">
          Monthly Upload Summary
        </div>
        <div className="flex flex-col gap-3">
          {sortedMonths.map((data, idx) => (
            <MonthlyCard
              key={data.month}
              data={data}
              onViewAll={handleViewAll}
              dynamicPct={idx === 0 ? completionPct : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
